<template>
    <div
        class="fixed top-4 right-4 z-[9999] w-full max-w-sm space-y-3 pointer-events-none"
        data-testid="notification-container-top-right"
    >
        <transition-group name="list-right" tag="div">
            <NotificationCard
                v-for="notification in topRightNotifications"
                :key="notification.id"
                :notification="notification"
                @remove="remove"
            />
        </transition-group>
    </div>

    <div
        class="fixed top-4 left-4 z-[9999] w-full max-w-sm space-y-3 pointer-events-none"
        data-testid="notification-container-top-left"
    >
        <transition-group name="list-left" tag="div">
            <NotificationCard
                v-for="notification in topLeftNotifications"
                :key="notification.id"
                :notification="notification"
                @remove="remove"
            />
        </transition-group>
    </div>

    <div
        class="fixed bottom-4 right-4 z-[9999] w-full max-w-sm space-y-3 pointer-events-none"
        data-testid="notification-container-bottom-right"
    >
        <transition-group name="list-right" tag="div">
            <NotificationCard
                v-for="notification in bottomRightNotifications"
                :key="notification.id"
                :notification="notification"
                @remove="remove"
            />
        </transition-group>
    </div>

    <div
        class="fixed bottom-4 left-4 z-[9999] w-full max-w-sm space-y-3 pointer-events-none"
        data-testid="notification-container-bottom-left"
    >
        <transition-group name="list-left" tag="div">
            <NotificationCard
                v-for="notification in bottomLeftNotifications"
                :key="notification.id"
                :notification="notification"
                @remove="remove"
            />
        </transition-group>
    </div>
</template>

<script setup>
import { computed, h } from "vue";
import { useNotification } from "~/composables/useNotification";

const { notifications, remove } = useNotification();

const topRightNotifications = computed(() =>
    notifications.value.filter((n) => n.position === "top-right")
);
const topLeftNotifications = computed(() =>
    notifications.value.filter((n) => n.position === "top-left")
);
const bottomRightNotifications = computed(() =>
    notifications.value.filter((n) => n.position === "bottom-right")
);
const bottomLeftNotifications = computed(() =>
    notifications.value.filter((n) => n.position === "bottom-left")
);

const NotificationCard = (props, { emit }) => {
    const { notification } = props;

    const styles = {
        info: {
            title: "Info",
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>`,
            container: "bg-blue-50 border-blue-400",
            iconColor: "text-blue-500",
            titleColor: "text-blue-800",
            messageColor: "text-blue-700",
        },
        success: {
            title: "Success",
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>`,
            container: "bg-green-50 border-green-400",
            iconColor: "text-green-500",
            titleColor: "text-green-800",
            messageColor: "text-green-700",
        },
        warning: {
            title: "Warning",
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>`,
            container: "bg-yellow-50 border-yellow-400",
            iconColor: "text-yellow-500",
            titleColor: "text-yellow-800",
            messageColor: "text-yellow-700",
        },
        error: {
            title: "Error",
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12" y2="16"></line></svg>`,
            container: "bg-red-50 border-red-400",
            iconColor: "text-red-500",
            titleColor: "text-red-800",
            messageColor: "text-red-700",
        },
    };

    const currentStyle = styles[notification.type];

    return h(
        "div",
        {
            class: `pointer-events-auto w-full rounded-lg shadow-xl overflow-hidden border-l-4 ${currentStyle.container}`,
            "data-testid": `notification-${notification.type}`,
        },
        [
            h("div", { class: "p-4" }, [
                h("div", { class: "flex items-start" }, [
                    h("div", {
                        class: "flex-shrink-0",
                        innerHTML: currentStyle.icon,
                        class: currentStyle.iconColor,
                        "data-testid": "notification-icon",
                    }),
                    h("div", { class: "ml-3 w-0 flex-1 pt-0.5" }, [
                        h(
                            "p",
                            {
                                class: `text-sm font-bold ${currentStyle.titleColor}`,
                                "data-testid": "notification-title",
                            },
                            currentStyle.title
                        ),
                        h(
                            "p",
                            {
                                class: `mt-1 text-sm ${currentStyle.messageColor}`,
                                "data-testid": "notification-message",
                            },
                            notification.message
                        ),
                    ]),
                    h("div", { class: "ml-4 flex-shrink-0 flex" }, [
                        h(
                            "button",
                            {
                                onClick: () => emit("remove", notification.id),
                                class: `inline-flex text-gray-400 rounded-md hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`,
                                "data-testid": "notification-close-button",
                            },
                            h("span", { class: "sr-only" }, "Close"),
                            h("span", {
                                innerHTML: `&times;`,
                                class: "text-2xl",
                            })
                        ),
                    ]),
                ]),
            ]),
        ]
    );
};
</script>

<style scoped>
.list-right-enter-active,
.list-right-leave-active {
    transition: all 0.4s ease-in-out;
}
.list-right-enter-from,
.list-right-leave-to {
    opacity: 0;
    transform: translateX(100%);
}
.list-left-enter-active,
.list-left-leave-active {
    transition: all 0.4s ease-in-out;
}
.list-left-enter-from,
.list-left-leave-to {
    opacity: 0;
    transform: translateX(-100%);
}
</style>
